import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { SousService } from 'app/models/sous-service';
import { ServicesService } from 'app/services/services/services.service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-ajouter-sous-service',
  templateUrl: './ajouter-sous-service.component.html',
  styleUrls: ['./ajouter-sous-service.component.scss']
})
export class AjouterSousServiceComponent implements OnInit {
  // Sous-service data
  sousService: SousService = new SousService();
  service: any;
  idService: string | null = null;

  // Images (listimages)
  uploadedImages: string[] = [];   // URLs of uploaded images (all will be stored in listimages)
  selectedFiles: FileList | null = null;
  isUploading: boolean = false;
  uploadProgress: number = 0;

  // Form state
  isSubmitting: boolean = false;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private serviceService: ServicesService,
    private sousServiceService: SousServiceService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.serviceService.refreshNedeed.subscribe(() => this.loadServiceDetails());
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.serviceService.getService(this.idService).subscribe({
        next: (data) => {
          this.service = data;
          console.log('Service loaded:', this.service);
        },
        error: (error) => {
          console.error('Error loading service:', error);
          Swal.fire('Erreur', 'Impossible de charger les détails du service', 'error');
        }
      });
    }
  }

  // ----- TEXT FORMATTING (optional) -----
  formatText(command: string): void {
    if (!this.sousService.description) this.sousService.description = '';
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousService.description.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
      case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
    }
    const newText = this.sousService.description.substring(0, start) + formattedText + this.sousService.description.substring(end);
    this.sousService.description = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // ----- IMAGE UPLOAD (multiple) -----
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedFiles = files;
      this.uploadImages();
      event.target.value = ''; // reset input
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (!validTypes.includes(file.type)) {
      Swal.fire('Format non supporté', 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', 'warning');
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire('Fichier trop volumineux', 'La taille maximale est de 5MB', 'warning');
      return false;
    }
    return true;
  }

  async uploadImages(): Promise<void> {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: '0%',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    try {
      const files = Array.from(this.selectedFiles);
      const totalFiles = files.length;
      let uploadedCount = 0;

      for (const file of files) {
        if (!this.validateImageFile(file)) {
          uploadedCount++;
          continue;
        }

        try {
          const response = await this.awsService.uploadFile(file, 'sous-services').toPromise();
          if (response.success && response.urls && response.urls.length > 0) {
            this.uploadedImages.push(response.urls[0]);
          } else {
            throw new Error(response.error || 'Upload failed');
          }
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          // Continue with other files
        } finally {
          uploadedCount++;
          this.uploadProgress = Math.round((uploadedCount / totalFiles) * 100);
          Swal.update({ html: `${this.uploadProgress}% (${uploadedCount}/${totalFiles})` });
        }
      }

      Swal.close();

      if (this.uploadedImages.length > 0) {
        Swal.fire({
          icon: 'success',
          title: 'Images téléchargées',
          text: `${this.uploadedImages.length} image(s) téléchargée(s) avec succès`,
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Aucune image téléchargée', 'Veuillez réessayer', 'error');
      }
    } catch (error) {
      Swal.close();
      Swal.fire('Erreur', 'Le téléchargement a échoué', 'error');
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
    }
  }

  removeImage(index: number): void {
    const imageUrl = this.uploadedImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(imageUrl).toPromise();
          this.uploadedImages.splice(index, 1);
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ----- FORM VALIDATION -----
  validateForm(): boolean {
    if (this.isUploading) {
      Swal.fire('Téléchargement en cours', 'Veuillez attendre la fin du téléchargement', 'warning');
      return false;
    }
    if (!this.sousService.name || this.sousService.name.trim() === '') {
      Swal.fire('Nom requis', 'Veuillez saisir un nom', 'warning');
      return false;
    }
    if (!this.sousService.description || this.sousService.description.trim() === '') {
      Swal.fire('Description requise', 'Veuillez saisir une description', 'warning');
      return false;
    }
    if (this.uploadedImages.length === 0) {
      Swal.fire('Image requise', 'Veuillez ajouter au moins une image', 'warning');
      return false;
    }
    return true;
  }

  // ----- SUBMIT -----
  onSubmit(): void {
    if (!this.validateForm()) return;
    if (!this.idService) {
      Swal.fire('Erreur', 'ID service manquant', 'error');
      return;
    }

    this.isSubmitting = true;
    Swal.fire({
      title: 'Création en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    // Prepare payload with listimages (all uploaded images)
    const sousServiceData = {
      name: this.sousService.name.trim(),
      website: this.sousService.website?.trim() || '',
      category: this.sousService.category?.trim() || '',
      description: this.sousService.description,
      prix: this.sousService.prix?.trim() || '',
      image_url: this.uploadedImages[0],   // first image as main cover
      listimages: this.uploadedImages       // all images for gallery
    };

    this.sousServiceService.createSousService(sousServiceData, this.idService).subscribe({
      next: () => {
        Swal.close();
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Sous-service créé avec succès',
          timer: 2000,
          showConfirmButton: false
        }).then(() => this.router.navigate(['listeSousServices']));
      },
      error: (error: HttpErrorResponse) => {
        Swal.close();
        this.isSubmitting = false;
        let errorMessage = 'Erreur lors de la création';
        if (error.status === 400) errorMessage = 'Données invalides. Vérifiez les champs.';
        else if (error.status === 401) errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        else if (error.status === 403) errorMessage = 'Permissions insuffisantes.';
        else if (error.status === 500) errorMessage = 'Erreur serveur. Réessayez plus tard.';
        Swal.fire('Erreur', errorMessage, 'error');
      }
    });
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser le formulaire ?',
      text: 'Toutes les données non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousService = new SousService();
        this.uploadedImages = [];
        this.selectedFiles = null;
        if (this.fileInput) this.fileInput.nativeElement.value = '';
        Swal.fire('Formulaire réinitialisé', '', 'success');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['listeServices']);
  }
}